import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { Query } from "@tanstack/react-query";

type RetryHandler = (
  failureCount: number,
  error: Error | object | null,
) => boolean;

interface QueryMeta {
  /** Optional human-readable error message used by query error handlers. */
  errorMessage?: string;
}
interface RequestOptions {
  [key: string]: string | number | boolean | undefined | null;
}

/**
 * Merges class names intelligently using `clsx` + `tailwind-merge`.
 *
 * - Removes duplicate Tailwind classes.
 * - Useful for conditional Tailwind class composition.
 *
 * @example
 * cn("p-2", condition && "bg-red-500")
 *
 * @param inputs - A list of class values.
 * @returns A merged class string.
 */
export const cn = (...inputs: ClassValue[]): string => {
  return twMerge(clsx(inputs));
};

/**
 * Converts a snake_case or kebab-case string into camelCase.
 *
 * @example
 * toCamelCase("energy_consumption") // "energyConsumption"
 *
 * @param key - The string key to convert.
 * @returns The camelCased version of the key.
 */
export const toCamelCase = (key: string): string => {
  return key.replace(/[_-]([a-zA-Z0-9])/g, (_, c) => c.toUpperCase());
};

/**
 * Recursively converts all object keys from snake_case or kebab-case to camelCase.
 *
 * @example
 * camelCaseObject({ energy_consumption: { trend_percent: 10 } })
 *
 * @typeParam T - The expected output type.
 * @param input - The input value to transform.
 * @returns A deeply camelCased version of the input.
 */
export const camelCaseObject = <T>(input: T): T => {
  if (input === null || input === undefined) {
    return input;
  }

  if (Array.isArray(input)) {
    return input.map((i) => camelCaseObject(i)) as unknown as T;
  }

  if (typeof input === "object") {
    const obj = input as Record<string, unknown>;
    const out: Record<string, unknown> = {};

    for (const key of Object.keys(obj)) {
      const camelKey = toCamelCase(key);
      out[camelKey] = camelCaseObject(obj[key]);
    }

    return out as T;
  }

  return input;
};

/**
 * Generates a stable queryKey array for React Query.
 *
 * - Ensures consistent caching between calls.
 * - Flattens `requestOptions` values into the key.
 *
 * @example
 * generateKey("kpis", 1, { period: "6h" })
 * // ["kpis", 1, "6h"]
 *
 * @param key - A unique string identifier for the query.
 * @param facilityId - Facility ID for scoping the query.
 * @param requestOptions - Optional parameters added into the query key.
 * @returns A stable array to be used as the React Query cache key.
 */
export const generateKey = (
  key: string,
  facilityId: number | string,
  requestOptions?: RequestOptions,
): Array<string | number | boolean | null | undefined> => {
  return [
    key,
    facilityId,
    ...(requestOptions ? Object.values(requestOptions) : []),
  ];
};

/**
 * Default retry handler for React Query.
 *
 * Behavior:
 * - Retries up to 3 times.
 * - Stops retrying immediately on HTTP 404/403 errors — retrying can't
 *   change "not found" or "no access", it only delays the error UI.
 *   Status is read from the axios error apiClient rejects with
 *   (`error.response.status`).
 *
 * @param failureCount - Number of failed attempts so far.
 * @param error - Error object or structured error response.
 * @returns `true` to retry, `false` to stop retrying.
 */
export const defaultQueryClientRetryHandler: RetryHandler = (
  failureCount,
  error,
): boolean => {
  const httpStatus = (error as { response?: { status?: number } } | null)
    ?.response?.status;

  return !(failureCount >= 3 || httpStatus === 404 || httpStatus === 403);
};

/**
 * Logs meaningful query errors during React Query's cache operations.
 *
 * - If `query.meta.errorMessage` exists, it logs that message.
 * - Otherwise logs the `Error.message` if available.
 * - Avoids throwing — safe for global error pipelines.
 *
 * @param error - The error object thrown by React Query.
 * @param query - The query instance that failed.
 */
export const queryCacheOnErrorHandler = (
  error: Error,
  query: Query<unknown, unknown, unknown, readonly unknown[]>,
): void => {
  // If authors placed a human-friendly message in query.meta, log it.
  const metaMessage = (query?.meta as QueryMeta | undefined)?.errorMessage;
  if (metaMessage) {
    // Replace with logger (Sentry/Datadog) in future
    console.error(metaMessage);
    return;
  }

  // Fallback: log the Error.message
  console.error(error.message);
};

/**
 * Formats a numeric value into a USD currency string.
 *
 * @function formatUsd
 * @param {number | undefined} value - The numeric value to format.
 * @returns {string | null} A formatted USD currency string (e.g. "$2,196"),
 *                          or null if the value is undefined or null.
 *
 * @example
 * formatUsd(2196) // "$2,196"
 * formatUsd(undefined) // null
 */
export const formatUsd = (value?: number): string | null => {
  if (value == null) return null;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(value);
};

/**
 * Determines whether a number is strictly positive.
 *
 * @function isPositive
 * @param {number | undefined} n - The number to evaluate.
 * @returns {boolean} True if n is a number greater than zero; false otherwise.
 *
 * @example
 * isPositive(10) // true
 */
export const isPositive = (number?: number): boolean =>
  typeof number === "number" && Math.sign(number) === 1;

/**
 * Formats an ISO timestamp string without converting timezone.
 * Preserves API time exactly as received.
 *
 * Example:
 * "2025-12-17T08:34:43+00:00" → "2025-12-17 08:34:43"
 */
export const formatTimestamp = (timestamp: string) => {
  if (!timestamp) return "";

  return timestamp.replace("T", " ").split(".")[0].replace("+00:00", "");
};

/**
 * Format an ISO string to YYYY-MM-DD.
 * @param iso - ISO date string (e.g., "2025-12-18T16:00:00Z")
 * @returns Formatted date string in "YYYY-MM-DD"
 */
export const formatDateOnly = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Formats a Date object into a human-readable display string.
 * @example "February 6 2026"
 * * @param date - The Date object to be formatted.
 * @returns A string containing the full month name, day, and year without commas.
 */
export const formatDisplayDate = (date: Date): string => {
  return date
    .toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    })
    .replace(",", "");
};

/**
 * Formats an ISO timestamp into a readable date-time string
 * using 12-hour (AM/PM) format.
 *
 * Output format:
 * YYYY-MM-DD hh:mm AM/PM
 *
 * Example:
 * formatTimestampToAmPm("2025-08-20T00:12:00.000Z")
 * // → "2025-08-20 12:12 AM"
 *
 * @param timestamp - ISO timestamp string
 * @returns Formatted date-time string or empty string if input is invalid
 */
export const formatTimestampWithAmPm = (timestamp: string) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);

  if (isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12; // convert 0 → 12
  const formattedHours = String(hours).padStart(2, "0");

  return `${year}-${month}-${day} ${formattedHours}:${minutes} ${ampm}`;
};

// compact formatting for display: 20000 -> "20k", 2500 -> "2.5k", 2000000 -> "2M"
export function formatCompactNumber(v: number) {
  const abs = Math.abs(v);
  if (abs >= 1_000_000)
    return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1000) {
    const k = v / 1000;
    return Number.isInteger(k) ? `${k}k` : `${+k.toFixed(1)}k`;
  }
  return `${v}`;
}

/**
 * Formats a number in "k" notation if it is 1000 or greater.
 *
 * Examples:
 *  - 500      => "500"
 *  - null     => "--"
 *  - 0        => "--"
 *
 * @param num - The number to format. Can be undefined or null.
 * @returns A string representation of the number in k-format or "--" for 0/undefined/null.
 */
export const formatK = (num?: number | null) => {
  if (num == null) return "-";
  if (num >= 1000) return (num / 1000).toFixed(1) + "k";
  return String(parseFloat(num.toFixed(2)));
};

/**
 * Calculates a human-readable relative time string (e.g., "In 2 hours", "5 mins ago")
 * between a given ISO timestamp and the current system time.
 * * @param timestamp - The ISO 8601 string to compare against the current time.
 * @returns A formatted string representing the relative time difference.
 * * @example
 * // If now is 12:00 and timestamp is 14:00
 * getRelativeTime("2026-01-29T14:00:00Z") // returns "In 2 hours"
 */
export const getRelativeTime = (timestamp: string | null | undefined) => {
  if (!timestamp) return "—";
  const eventDate = new Date(timestamp);
  const now = new Date();
  const diffInMs = eventDate.getTime() - now.getTime();
  // Round to nearest minute for display purposes
  const diffInMinutes = Math.round(diffInMs / (1000 * 60));

  // Future dates (Upcoming decisions)
  if (diffInMinutes > 0) {
    if (diffInMinutes < 60) return `In ${diffInMinutes} mins`;
    // Use hours + remaining minutes to preserve precision for closely-spaced events
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;
    return minutes > 0 ? `In ${hours}h ${minutes}m` : `In ${hours}h`;
  }

  // Past dates (Recent events)
  const absMinutes = Math.abs(diffInMinutes);

  if (absMinutes < 1) return "Just now";
  if (absMinutes < 60) return `${absMinutes} mins ago`;
  // Use hours + remaining minutes instead of rounding to whole hours,
  // so events within the same hour window get distinct labels
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m ago` : `${hours}h ago`;
};

/**
 * Triggers a browser file download by navigating directly to the given URL.
 * Uses an anchor click instead of XHR so no CORS check is performed —
 * required when the URL is a pre-signed S3 redirect that strips the Origin header.
 */
export const triggerDownload = (href: string, filename: string): void => {
  const anchorEl = document.createElement("a");
  anchorEl.href = href;
  anchorEl.download = filename;
  document.body.appendChild(anchorEl);
  anchorEl.click();
  document.body.removeChild(anchorEl);
};

/**
 * Format ISO timestamp to "YYYY-MM-DD hh:mm:ss AM/PM" without converting to local time
 * @param isoTimestamp e.g. "2026-02-28T05:45:00-07:00"
 * @returns string like "2026-02-28 5:45:00 AM"
 */
export const formatIsoTimestampWithOffset = (
  isoTimestamp: string | undefined,
): string => {
  if (!isoTimestamp) return "";

  // Split date and time parts from the ISO string
  const [datePart, timePartWithOffset] = isoTimestamp.split("T");
  const [timePart] = timePartWithOffset.split(/[-+]/); // remove offset

  // Extract hours, minutes, and seconds
  const [hoursStr, minutesStr, secondsStr] = timePart.split(":");
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  const seconds = parseInt(secondsStr ?? "0", 10);

  // Convert to 12-hour format
  const period = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;

  return `${datePart} ${hour12}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")} ${period}`;
};

/**
 * Formats an ISO timestamp's time-of-day plus its UTC offset, without
 * converting to the viewer's local timezone.
 *
 * `new Date(iso).toLocaleTimeString()` parses the offset correctly but then
 * renders in the *browser's* timezone, silently discarding the offset the
 * API sent — the offset is read directly out of the string instead so the
 * displayed time always matches what the API/facility reported.
 *
 * @param isoTimestamp e.g. "2026-09-08T18:26:04.847845-04:00"
 * @returns string like "06:26 PM UTC-04:00", or "" if the timestamp is
 *          missing or has no timezone designator (Z or ±HH:MM).
 */
export const formatTimeWithOffset = (
  isoTimestamp: string | undefined,
): string => {
  if (!isoTimestamp) return "";

  const match = isoTimestamp.match(
    /T(\d{2}):(\d{2}):\d{2}(?:\.\d+)?(Z|[+-]\d{2}:\d{2})$/,
  );
  if (!match) return "";

  const [, hoursStr, minutesStr, offsetRaw] = match;
  const hours = parseInt(hoursStr, 10);

  const period = hours >= 12 ? "PM" : "AM";
  const hour12 = (hours % 12 || 12).toString().padStart(2, "0");

  const offset = offsetRaw === "Z" ? "UTC+00:00" : `UTC${offsetRaw}`;

  return `${hour12}:${minutesStr} ${period} ${offset}`;
};
